import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Lock, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AppointmentModal } from "@/components/AppointmentModal";
import { useAppState } from "@/context/AppState";
import { isSameDay, formatHora, addDays, formatPrecio, dataApi } from "@/lib/agenda";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const DOW = ["L", "M", "X", "J", "V", "S", "D"];
const DIAS_LARGO = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const SLOTS = Array.from({ length: 26 }, (_, i) => ({ h: 8 + Math.floor(i / 2), m: i % 2 === 0 ? 0 : 30 })); // 08:00 a 20:30 cada 30 min

const pad = (n) => String(n).padStart(2, "0");
const fmtYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localDT = (d, h, m = 0) => `${fmtYMD(d)}T${pad(h)}:${pad(m)}`;

const buildCells = (year, month) => {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - startOffset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

const weekStart = (d) => {
  const x = new Date(d);
  const off = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - off);
  x.setHours(0, 0, 0, 0);
  return x;
};

export default function Calendario() {
  const { appointments, therapists, addAppointment, updateAppointment } = useAppState();
  const today = new Date();
  const [view, setView] = useState("mes");
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(today);
  const [filterTid, setFilterTid] = useState("todos");
  const [franjaTid, setFranjaTid] = useState("");
  const [search, setSearch] = useState("");

  const [availWeek, setAvailWeek] = useState([]);
  const [blocks, setBlocks] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const dragRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [conflictMsg, setConflictMsg] = useState("");

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const cells = buildCells(year, month);

  const refId = filterTid !== "todos" ? filterTid : franjaTid || therapists[0]?.id || "";
  const refTherapist = therapists.find((t) => t.id === refId);

  useEffect(() => {
    if (!refId) {
      setAvailWeek([]);
      setBlocks([]);
      return;
    }
    let active = true;
    Promise.all([dataApi.getAvailability(refId), dataApi.listBlocks(refId)])
      .then(([av, bl]) => {
        if (!active) return;
        setAvailWeek(av.week || []);
        setBlocks(bl || []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [refId]);

  const colorOf = (appt) => therapists.find((t) => t.id === appt.therapistId)?.color || "#275FBE";
  const minutesOf = (a) => { const d = new Date(a.appointment_datetime); return d.getHours() * 60 + d.getMinutes(); };
  const durOf = (a) => a.duration || 60;
  const visibleAppts = filterTid === "todos" ? appointments : appointments.filter((a) => a.therapistId === filterTid);
  const apptsOf = (date) =>
    visibleAppts
      .filter((a) => isSameDay(a.appointment_datetime, date))
      .sort((a, b) => new Date(a.appointment_datetime) - new Date(b.appointment_datetime));

  const blockCovers = (b, ymd) => b.date <= ymd && ymd <= (b.end_date || b.date);
  const dayHasBlock = (date) => blocks.some((b) => blockCovers(b, fmtYMD(date)));
  const dayFullBlock = (date) => blocks.some((b) => b.all_day && blockCovers(b, fmtYMD(date)));
  const parseT = (t) => { const [hh, mm] = String(t || "0:0").split(":").map(Number); return hh * 60 + (mm || 0); };
  const slotBlocked = (date, h, m) => {
    const ymd = fmtYMD(date);
    const s = h * 60 + m;
    const e = s + 30;
    return blocks.some((b) => blockCovers(b, ymd) && (b.all_day || (parseT(b.from_time) < e && s < parseT(b.to_time))));
  };
  const hourWorking = (date, h) => {
    if (!availWeek.length) return true;
    const row = availWeek[(date.getDay() + 6) % 7];
    if (!row) return true;
    return [...(row.morning || []), ...(row.afternoon || [])].some((v) => Math.floor(v) === h);
  };

  const getConflict = (dt, tid, excludeId, dur = 60) => {
    const h = dt.getHours();
    const m = dt.getMinutes() >= 30 ? 30 : 0;
    const newStart = h * 60 + dt.getMinutes();
    const newEnd = newStart + dur;
    let occupied = false;
    let outside = false;
    for (let t = newStart; t < newEnd; t += 30) {
      const hh = Math.floor(t / 60);
      const mm = t % 60 >= 30 ? 30 : 0;
      if (slotBlocked(dt, hh, mm)) occupied = true;
      if (!hourWorking(dt, hh, mm)) outside = true;
    }
    const overlap = appointments.some((a) => {
      if (a.id === excludeId || a.therapistId !== tid || !isSameDay(a.appointment_datetime, dt)) return false;
      const s = minutesOf(a);
      return s < newEnd && newStart < s + durOf(a);
    });
    const parts = [];
    if (overlap) parts.push("se solapa con otra cita");
    if (occupied) parts.push("esa hora está marcada como ocupada");
    if (outside) parts.push("cae fuera de tu horario de trabajo");
    return parts.length ? `Esta cita ${parts.join(" y ")}.` : null;
  };

  const handlePrev = () => {
    if (view === "mes") setCursor(new Date(year, month - 1, 1));
    else if (view === "semana") setSelected(addDays(selected, -7));
    else setCursor(new Date(year - 1, month, 1));
  };
  const handleNext = () => {
    if (view === "mes") setCursor(new Date(year, month + 1, 1));
    else if (view === "semana") setSelected(addDays(selected, 7));
    else setCursor(new Date(year + 1, month, 1));
  };
  const goToday = () => {
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelected(today);
  };

  const jumpToAppt = (appt) => {
    const d = new Date(appt.appointment_datetime);
    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
    setSelected(d);
    setView("mes");
    setSearch("");
  };

  const openCreate = (date, h, m = 0) => {
    setEditing(null);
    setCreating({ therapistId: refId || therapists[0]?.id || "", appointment_datetime: localDT(date, h, m) });
    setModalOpen(true);
  };
  const openEdit = (appt) => {
    setCreating(null);
    setEditing(appt);
    setModalOpen(true);
  };

  const doSave = async (payload, isEdit, editId) => {
    try {
      if (isEdit) {
        await updateAppointment(editId, payload);
        toast.success("Cita actualizada.");
      } else {
        await addAppointment(payload);
        toast.success("Listo. Tu cita está guardada.");
      }
      setModalOpen(false);
      setEditing(null);
      setCreating(null);
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  const doMove = async (appt, nd) => {
    try {
      await updateAppointment(appt.id, { appointment_datetime: nd.toISOString() });
      toast.success("Cita movida.");
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  const handleSave = (payload) => {
    const isEdit = !!editing;
    const editId = editing?.id;
    const dt = new Date(payload.appointment_datetime);
    const conflict = getConflict(dt, payload.therapistId, editId, payload.duration);
    if (conflict) {
      setConflictMsg(conflict);
      setPending({ type: "save", payload, isEdit, editId });
      return;
    }
    doSave(payload, isEdit, editId);
  };

  const handleDrop = (day, h, m) => {
    const dragId = dragRef.current;
    if (!dragId) return;
    const appt = appointments.find((a) => a.id === dragId);
    dragRef.current = null;
    if (!appt) return;
    const orig = new Date(appt.appointment_datetime);
    const nd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, m);
    if (nd.getTime() === orig.getTime()) return;
    const conflict = getConflict(nd, appt.therapistId, appt.id, durOf(appt));
    if (conflict) {
      setConflictMsg(conflict);
      setPending({ type: "move", appt, nd });
      return;
    }
    doMove(appt, nd);
  };

  const ws = weekStart(selected);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  const selectedAppts = apptsOf(selected);
  const cobrado = selectedAppts.filter((a) => a.payment_status === "pagado").reduce((s, a) => s + (a.price || 0), 0);
  const pendiente = selectedAppts.filter((a) => a.payment_status !== "pagado").reduce((s, a) => s + (a.price || 0), 0);
  const wkAppts = visibleAppts.filter((a) => { const d = new Date(a.appointment_datetime); return d >= ws && d < addDays(ws, 7); });
  const wkCobrado = wkAppts.filter((a) => a.payment_status === "pagado").reduce((s, a) => s + (a.price || 0), 0);
  const wkPendiente = wkAppts.filter((a) => a.payment_status !== "pagado").reduce((s, a) => s + (a.price || 0), 0);
  const monthAppts = visibleAppts.filter((a) => { const d = new Date(a.appointment_datetime); return d.getFullYear() === year && d.getMonth() === month; });
  const moCobrado = monthAppts.filter((a) => a.payment_status === "pagado").reduce((s, a) => s + (a.price || 0), 0);
  const moPendiente = monthAppts.filter((a) => a.payment_status !== "pagado").reduce((s, a) => s + (a.price || 0), 0);
  const title =
    view === "mes"
      ? `${MESES[month]} ${year}`
      : view === "anio"
      ? `${year}`
      : `${ws.getDate()} ${MESES[ws.getMonth()].slice(0, 3)} – ${weekDays[6].getDate()} ${MESES[weekDays[6].getMonth()].slice(0, 3)}`;

  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const searchResults = search.trim()
    ? appointments
        .filter((a) => (a.client_name || "").toLowerCase().includes(search.trim().toLowerCase()) && new Date(a.appointment_datetime) >= startToday)
        .sort((a, b) => new Date(a.appointment_datetime) - new Date(b.appointment_datetime))
        .slice(0, 6)
    : [];

  return (
    <div className="space-y-5 animate-fade-up" data-testid="calendario-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Calendario</h1>
          <p className="text-sm text-slate-500 mt-1">Mira tu mes o tu semana de un vistazo.</p>
        </div>
        <div className="flex rounded-full bg-slate-100 p-1">
          <button onClick={() => setView("mes")} data-testid="view-mes" className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === "mes" ? "bg-white shadow text-slate-800" : "text-slate-500"}`}>Mes</button>
          <button onClick={() => setView("semana")} data-testid="view-semana" className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === "semana" ? "bg-white shadow text-slate-800" : "text-slate-500"}`}>Semana</button>
          <button onClick={() => setView("anio")} data-testid="view-anio" className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === "anio" ? "bg-white shadow text-slate-800" : "text-slate-500"}`}>Año</button>
        </div>
      </div>

      <div className="relative max-w-md" data-testid="calendar-search">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (searchResults[0]) jumpToAppt(searchResults[0]);
              else if (search.trim()) toast("No hay próximas citas de ese cliente.");
            }
          }}
          placeholder="Busca un cliente y salta a su próxima cita"
          data-testid="calendar-search-input"
          className="w-full h-10 pl-9 pr-3 rounded-full border border-slate-200 text-sm bg-white focus:outline-none focus:border-[#275FBE]"
        />
        {searchResults.length > 0 && (
          <div className="absolute z-20 mt-1 w-full rounded-2xl border border-[#EAE5DF] bg-white shadow-lg overflow-hidden" data-testid="search-results">
            {searchResults.map((a) => (
              <button
                key={a.id}
                onClick={() => jumpToAppt(a)}
                data-testid={`search-result-${a.id}`}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-slate-50"
              >
                <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: colorOf(a) }} />
                <span className="font-medium text-slate-800">{a.client_name}</span>
                <span className="text-slate-400 ml-auto">{new Date(a.appointment_datetime).getDate()} {MESES[new Date(a.appointment_datetime).getMonth()].slice(0, 3)} · {formatHora(a.appointment_datetime)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {therapists.length > 1 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="therapist-filter">
          <button
            onClick={() => setFilterTid("todos")}
            data-testid="filter-todos"
            className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${filterTid === "todos" ? "text-white border-transparent" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
            style={filterTid === "todos" ? { backgroundColor: "#2E2A8F" } : {}}
          >
            Todos
          </button>
          {therapists.map((t) => (
            <button
              key={t.id}
              onClick={() => setFilterTid(t.id)}
              data-testid={`filter-therapist-${t.id}`}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${filterTid === t.id ? "text-white border-transparent" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
              style={filterTid === t.id ? { backgroundColor: t.color } : {}}
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: filterTid === t.id ? "#fff" : t.color }} />
              {t.name}
            </button>
          ))}
        </div>
      )}

      <div className="rounded-3xl border border-[#EAE5DF] bg-white shadow-[0_18px_50px_-28px_rgba(46,42,143,0.22)] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-[#FAF7F2] border-b border-[#EAE5DF]">
          <h2 className="font-heading text-lg font-semibold text-slate-800 capitalize" data-testid="calendar-title">{title}</h2>
          <div className="flex items-center gap-2">
            <button onClick={goToday} data-testid="calendar-today" className="px-3 py-1.5 rounded-full text-sm font-medium border border-slate-200 text-slate-600 hover:border-slate-300 transition-colors">Hoy</button>
            <button onClick={handlePrev} data-testid="calendar-prev" aria-label="Anterior" className="h-9 w-9 flex items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:border-slate-300 transition-colors"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={handleNext} data-testid="calendar-next" aria-label="Siguiente" className="h-9 w-9 flex items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:border-slate-300 transition-colors"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>

        {view === "mes" ? (
          <div className="p-4 sm:p-6">
            <div className="flex flex-wrap gap-3 mb-4" data-testid="month-summary">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-2">
                <p className="text-xs text-emerald-700">Cobrado este mes</p>
                <p className="font-heading font-bold text-emerald-800" data-testid="month-cobrado">{formatPrecio(moCobrado)}</p>
              </div>
              <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-2">
                <p className="text-xs text-amber-700">Pendiente este mes</p>
                <p className="font-heading font-bold text-amber-800" data-testid="month-pendiente">{formatPrecio(moPendiente)}</p>
              </div>
            </div>
            <div className="grid grid-cols-7 mb-2">
              {DOW.map((d, i) => (
                <div key={i} className="text-center text-xs font-semibold uppercase tracking-wide text-slate-400 py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1.5" data-testid="calendar-grid">
              {cells.map((date, i) => {
                const inMonth = date.getMonth() === month;
                const isToday = isSameDay(date.toISOString(), today);
                const isSelected = isSameDay(date.toISOString(), selected);
                const dayAppts = apptsOf(date);
                const blocked = dayHasBlock(date);
                const fullBlocked = dayFullBlock(date);
                return (
                  <button
                    key={i}
                    onClick={() => setSelected(new Date(date))}
                    data-testid={`calendar-day-${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`}
                    className={`relative aspect-square rounded-xl flex flex-col items-center justify-center text-sm transition-all duration-200 border ${
                      isToday ? "text-white border-transparent shadow-md"
                        : isSelected ? "border-[#275FBE] bg-[#275FBE]/5 text-slate-800"
                        : fullBlocked ? "border-transparent bg-slate-100 text-slate-500"
                        : inMonth ? "border-transparent text-slate-700 hover:bg-slate-100"
                        : "border-transparent text-slate-300 hover:bg-slate-50"
                    }`}
                    style={isToday ? { backgroundColor: "#275FBE" } : {}}
                  >
                    {blocked && <Lock className={`absolute top-1.5 left-1.5 h-3 w-3 ${isToday ? "text-white/80" : "text-slate-400"}`} data-testid="day-blocked" />}
                    <span className={`${isToday ? "font-bold" : inMonth ? "font-medium" : ""}`}>{date.getDate()}</span>
                    {dayAppts.length > 0 && (
                      <span className="flex items-center gap-0.5 mt-1" data-testid="day-dots">
                        {dayAppts.slice(0, 3).map((a) => (
                          <span key={a.id} className={`h-1.5 w-1.5 rounded-full ${isToday ? "ring-1 ring-white" : ""}`} style={{ backgroundColor: colorOf(a) }} />
                        ))}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ) : view === "semana" ? (
          <>
            <div className="px-6 pt-4 flex flex-wrap gap-3" data-testid="week-summary">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-2">
                <p className="text-xs text-emerald-700">Cobrado esta semana</p>
                <p className="font-heading font-bold text-emerald-800" data-testid="week-cobrado">{formatPrecio(wkCobrado)}</p>
              </div>
              <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-2">
                <p className="text-xs text-amber-700">Pendiente esta semana</p>
                <p className="font-heading font-bold text-amber-800" data-testid="week-pendiente">{formatPrecio(wkPendiente)}</p>
              </div>
            </div>
            {filterTid === "todos" && therapists.length > 1 && (
              <div className="px-6 pt-4 flex items-center gap-2 text-sm text-slate-600" data-testid="franja-selector">
                <span>Franja de trabajo de:</span>
                <select
                  value={franjaTid || therapists[0]?.id || ""}
                  onChange={(e) => setFranjaTid(e.target.value)}
                  data-testid="franja-select"
                  className="rounded-lg border border-slate-200 px-2.5 py-1 text-sm bg-white"
                >
                  {therapists.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="overflow-x-auto" data-testid="week-grid">
              <div className="min-w-[760px]">
                <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-[#EAE5DF]">
                  <div className="py-3" />
                  {weekDays.map((d, i) => {
                    const isToday = isSameDay(d.toISOString(), today);
                    return (
                      <div key={i} className="py-3 text-center">
                        <div className="text-xs uppercase tracking-wide text-slate-400">{DOW[i]}</div>
                        <div className={`mt-1 mx-auto h-8 w-8 flex items-center justify-center rounded-full text-sm font-semibold ${isToday ? "text-white" : "text-slate-700"}`} style={isToday ? { backgroundColor: "#275FBE" } : {}}>{d.getDate()}</div>
                      </div>
                    );
                  })}
                </div>

                {SLOTS.map(({ h, m }) => (
                  <div key={`${h}-${m}`} className={`grid grid-cols-[60px_repeat(7,1fr)] border-b ${m === 30 ? "border-[#EAE5DF]" : "border-[#F6F2EC]"} last:border-b-0`}>
                    <div className="py-1.5 pr-2 text-right text-xs text-slate-400">
                      {m === 0 ? `${pad(h)}:00` : <span className="text-slate-300">{pad(h)}:30</span>}
                    </div>
                    {weekDays.map((d, i) => {
                      const slotStart = h * 60 + m;
                      const dayAppts = apptsOf(d);
                      const starting = dayAppts.filter((a) => minutesOf(a) === slotStart);
                      const continuation = dayAppts.filter((a) => minutesOf(a) < slotStart && minutesOf(a) + durOf(a) > slotStart);
                      const covered = starting.length > 0 || continuation.length > 0;
                      const blocked = slotBlocked(d, h, m);
                      const working = hourWorking(d, h, m);
                      const bg = blocked ? "bg-slate-200/70" : !working ? "bg-[#F4F1EC]" : "";
                      return (
                        <div
                          key={i}
                          onClick={() => !blocked && !covered && openCreate(d, h, m)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => { e.preventDefault(); handleDrop(d, h, m); }}
                          data-testid={`week-cell-${fmtYMD(d)}-${h}-${m}`}
                          className={`relative min-h-[26px] border-l border-[#F0EBE4] px-1 py-0.5 space-y-0.5 ${bg} ${blocked || covered ? "cursor-default" : "cursor-pointer hover:bg-[#275FBE]/5"}`}
                        >
                          {blocked && !covered && m === 0 && (
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-slate-400 pointer-events-none">Ocupado</span>
                          )}
                          {continuation.map((a) => (
                            <div
                              key={`c-${a.id}`}
                              onClick={(e) => { e.stopPropagation(); openEdit(a); }}
                              data-testid={`week-appt-cont-${a.id}`}
                              className="rounded-b-md -mt-0.5 h-5 cursor-pointer hover:opacity-90"
                              style={{ backgroundColor: colorOf(a) }}
                              title={`${formatHora(a.appointment_datetime)} · ${a.client_name}`}
                            />
                          ))}
                          {starting.map((a) => (
                            <div
                              key={a.id}
                              draggable
                              onDragStart={(e) => { dragRef.current = a.id; e.dataTransfer.effectAllowed = "move"; }}
                              onClick={(e) => { e.stopPropagation(); openEdit(a); }}
                              data-testid={`week-appt-${a.id}`}
                              className={`${durOf(a) > 30 ? "rounded-t-md" : "rounded-md"} px-2 py-0.5 text-[11px] leading-tight text-white truncate cursor-grab active:cursor-grabbing hover:opacity-90`}
                              style={{ backgroundColor: colorOf(a) }}
                              title={`${formatHora(a.appointment_datetime)} · ${a.client_name} · ${durOf(a)} min`}
                            >
                              {formatHora(a.appointment_datetime)} {a.client_name}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="year-grid">
              {Array.from({ length: 12 }, (_, mi) => {
                const mAppts = visibleAppts.filter((a) => { const d = new Date(a.appointment_datetime); return d.getFullYear() === year && d.getMonth() === mi; });
                const c = mAppts.filter((a) => a.payment_status === "pagado").reduce((s, a) => s + (a.price || 0), 0);
                const p = mAppts.filter((a) => a.payment_status !== "pagado").reduce((s, a) => s + (a.price || 0), 0);
                const isCur = mi === today.getMonth() && year === today.getFullYear();
                return (
                  <button
                    key={mi}
                    onClick={() => { setCursor(new Date(year, mi, 1)); setView("mes"); }}
                    data-testid={`year-month-${mi}`}
                    className={`text-left rounded-2xl border p-4 transition-colors hover:border-[#275FBE] ${isCur ? "border-[#275FBE] bg-[#275FBE]/5" : "border-[#EAE5DF] bg-white"}`}
                  >
                    <p className="font-heading font-semibold text-slate-800 capitalize mb-2">{MESES[mi]}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-emerald-700">Cobrado</span>
                      <span className="font-semibold text-emerald-800" data-testid={`year-cobrado-${mi}`}>{formatPrecio(c)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-amber-700">Pendiente</span>
                      <span className="font-semibold text-amber-800" data-testid={`year-pendiente-${mi}`}>{formatPrecio(p)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500" data-testid="calendar-legend">
        {therapists.map((t) => (
          <span key={t.id} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />
            {t.name}
          </span>
        ))}
        {view === "semana" && (
          <>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-[#F4F1EC] border border-slate-200" />Fuera de horario{refTherapist ? ` · ${refTherapist.name}` : ""}</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-slate-200/70 border border-slate-300" />Ocupado</span>
            <span className="text-slate-400">Arrastra una cita para moverla</span>
          </>
        )}
      </div>

      {view === "mes" && (
        <div className="rounded-3xl border border-[#EAE5DF] bg-white shadow-[0_18px_50px_-28px_rgba(46,42,143,0.22)] p-6" data-testid="calendar-day-panel">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-heading text-base font-semibold text-slate-800 capitalize">
              {DIAS_LARGO[selected.getDay()]} {selected.getDate()} de {MESES[selected.getMonth()]}
            </h3>
            <Button onClick={() => openCreate(selected, 9)} size="sm" className="rounded-full text-white gap-1.5" style={{ backgroundColor: "#275FBE" }} data-testid="add-appt-day">
              <Plus className="h-4 w-4" />
              Añadir cita
            </Button>
          </div>

          {selectedAppts.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-4" data-testid="day-summary">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-2.5">
                <p className="text-xs text-emerald-700">Cobrado</p>
                <p className="font-heading font-bold text-emerald-800" data-testid="day-cobrado">{formatPrecio(cobrado)}</p>
              </div>
              <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-2.5">
                <p className="text-xs text-amber-700">Pendiente de cobro</p>
                <p className="font-heading font-bold text-amber-800" data-testid="day-pendiente">{formatPrecio(pendiente)}</p>
              </div>
            </div>
          )}

          {selectedAppts.length === 0 ? (
            <p className="text-sm text-slate-400 mt-3">Sin citas este día.</p>
          ) : (
            <div className="mt-4 space-y-2">
              {selectedAppts.map((a) => (
                <button key={a.id} onClick={() => openEdit(a)} data-testid={`calendar-appt-${a.id}`} className="w-full flex items-center gap-3 rounded-2xl border border-[#F0EBE4] bg-[#FDFBF8] px-4 py-3 text-left hover:border-slate-300 transition-colors">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: colorOf(a) }} />
                  <span className="font-heading font-semibold text-slate-800 text-sm w-14 shrink-0">{formatHora(a.appointment_datetime)}</span>
                  <span className="text-sm text-slate-700 flex-1">{a.client_name}</span>
                  <span className={`text-xs font-medium ${a.payment_status === "pagado" ? "text-emerald-600" : "text-amber-600"}`}>
                    {a.payment_status === "pagado" ? "Pagado" : "Pendiente"} · {formatPrecio(a.price)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <AppointmentModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); setCreating(null); }}
        onSave={handleSave}
        appointment={editing || creating}
        forceNew={!editing && !!creating}
        therapists={therapists}
      />

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="sm:max-w-[400px] rounded-3xl" data-testid="overlap-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900">Revisa esta cita</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {conflictMsg} ¿Quieres guardarla igualmente?
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => setPending(null)} data-testid="overlap-cancel">Revisar</Button>
            <Button
              className="flex-1 rounded-full text-white"
              style={{ backgroundColor: "#275FBE" }}
              onClick={() => { const p = pending; setPending(null); if (p.type === "move") doMove(p.appt, p.nd); else doSave(p.payload, p.isEdit, p.editId); }}
              data-testid="overlap-confirm"
            >
              Guardar igualmente
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
